// Client-side share-card renderer using Canvas 2D — no heavy deps.
// Produces a 1080x1080 PNG Blob suitable for Capacitor Share / Web Share API.
// Designed against the 2026 Dub Brand Guide:
//   - cream background with soft lemon (top-right) and lavender (bottom-left) radial glows
//   - "dub" wordmark in ink, bold, lowercase (never tinted)
//   - ink-purple rounded row cards with lavender text (matches Core Message card)
//   - user's own row uses the lemon→mint CTA gradient with ink text (the "dub" moment)
//   - small purple pill labels in ALL CAPS
//   - Bricolage Grotesque for display, DM Sans for body

const SIZE = 1080
const PADDING = 72

const BRAND = {
  ink: '#191522',
  night: '#2A2239',
  gray: '#66626E',
  royalPurple: '#460069',
  purple: '#6C509B',
  lavender: '#E5D5FF',
  mint: '#C6FF9C',
  lemon: '#EBFF59',
  cream: '#F6F1F1',
  white: '#FFFFFF',
}

const HEADING_FONT = "'Bricolage Grotesque', system-ui, -apple-system, sans-serif"
const BODY_FONT = "'DM Sans', system-ui, -apple-system, sans-serif"

function roundedRect(ctx, x, y, width, height, radius) {
  ctx.beginPath()
  ctx.moveTo(x + radius, y)
  ctx.arcTo(x + width, y, x + width, y + height, radius)
  ctx.arcTo(x + width, y + height, x, y + height, radius)
  ctx.arcTo(x, y + height, x, y, radius)
  ctx.arcTo(x, y, x + width, y, radius)
  ctx.closePath()
}

function truncate(ctx, text, maxWidth) {
  if (!text) return ''
  if (ctx.measureText(text).width <= maxWidth) return text
  const ellipsis = '…'
  let result = text
  while (result.length > 1 && ctx.measureText(result + ellipsis).width > maxWidth) {
    result = result.slice(0, -1)
  }
  return result + ellipsis
}

// Word-wrap into at most `maxLines` lines. Anything past the last line gets
// truncated with an ellipsis so we never spill into the cards below. Splits on
// spaces and ` • ` separators so subtitles like "Tournament • Division • N
// entries" break naturally between segments.
function wrapLines(ctx, text, maxWidth, maxLines = 2) {
  if (!text) return []
  if (ctx.measureText(text).width <= maxWidth) return [text]
  const words = text.split(/(\s+)/).filter(Boolean)
  const lines = []
  let current = ''
  for (const token of words) {
    const candidate = current + token
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate
    } else {
      if (current.trim()) lines.push(current.trim())
      if (lines.length >= maxLines) break
      current = token.trim() ? token : ''
    }
  }
  if (current.trim() && lines.length < maxLines) lines.push(current.trim())
  if (lines.length === maxLines) {
    // If anything was dropped, ellipsize the last line.
    const consumed = lines.join(' ')
    if (consumed.length < text.length) {
      const remainder = text.slice(consumed.length).trim()
      if (remainder) lines[maxLines - 1] = truncate(ctx, lines[maxLines - 1] + ' ' + remainder, maxWidth)
    }
  }
  return lines
}

function drawGlow(ctx, cx, cy, radius, stops) {
  if (typeof ctx.createRadialGradient !== 'function') return
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius)
  stops.forEach(([stop, color]) => g.addColorStop(stop, color))
  ctx.fillStyle = g
  ctx.fillRect(0, 0, SIZE, SIZE)
}

// Lucide Crown geometry, baked from the icon used on /home in the
// "Champions" stat card. The 24x24 viewBox is scaled to fit `size`; the
// visible crown content occupies y=3..21 inside that box, so calling with
// y = midY - size/2 centers the crown vertically on midY.
const LUCIDE_CROWN_BODY = 'M11.562 3.266a.5.5 0 0 1 .876 0L15.39 8.87a1 1 0 0 0 1.516.294L21.183 5.5a.5.5 0 0 1 .798.519l-2.834 10.246a1 1 0 0 1-.956.734H5.81a1 1 0 0 1-.957-.734L2.02 6.02a.5.5 0 0 1 .798-.519l4.276 3.664a1 1 0 0 0 1.516-.294z'
const LUCIDE_CROWN_BAND = 'M5 21H19'

function drawCrown(ctx, x, y, size, color) {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(size / 24, size / 24)

  ctx.fillStyle = color
  ctx.fill(new Path2D(LUCIDE_CROWN_BODY))

  // Crown band — lucide draws this as a stroked horizontal line beneath
  // the body. Round caps match the rest of the lucide line geometry.
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.stroke(new Path2D(LUCIDE_CROWN_BAND))

  ctx.restore()
}

// Lucide "medal" (rank 2) and "award" (rank 3), drawn as strokes to match the
// leaderboard's getRankIcon. Same 24x24 viewBox + centering convention as the
// crown above.
function drawMedal(ctx, x, y, size, color) {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(size / 24, size / 24)
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke(new Path2D('M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15'))
  ctx.stroke(new Path2D('M11 12 5.12 2.2'))
  ctx.stroke(new Path2D('m13 12 5.88-9.8'))
  ctx.stroke(new Path2D('M8 7h8'))
  ctx.beginPath()
  ctx.arc(12, 17, 5, 0, Math.PI * 2)
  ctx.stroke()
  ctx.stroke(new Path2D('M12 18v-2h-.5'))
  ctx.restore()
}

function drawAward(ctx, x, y, size, color) {
  ctx.save()
  ctx.translate(x, y)
  ctx.scale(size / 24, size / 24)
  ctx.strokeStyle = color
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.beginPath()
  ctx.arc(12, 8, 6, 0, Math.PI * 2)
  ctx.stroke()
  ctx.stroke(new Path2D('M15.477 12.89 17 22l-5-3-5 3 1.523-9.11'))
  ctx.restore()
}

function drawPillLabel(ctx, text, x, y) {
  ctx.font = `700 20px ${BODY_FONT}`
  const padX = 14
  const padY = 8
  const width = ctx.measureText(text).width + padX * 2
  const height = 32
  ctx.fillStyle = BRAND.purple
  roundedRect(ctx, x, y, width, height, height / 2)
  ctx.fill()
  ctx.fillStyle = BRAND.white
  ctx.fillText(text, x + padX, y + padY)
  return { width, height }
}

// ── Design variants ──────────────────────────────────────────────────
// Owners want to compare share-card looks side by side in the Preview modal
// and pick a direction. Each variant is the same data drawn with a different
// theme; keep this list in sync with the carousel.
export const SHARE_CARD_VARIANTS = [
  { key: 'classic', label: 'Classic' },
  { key: 'detailed', label: 'Detailed' },   // adds accuracy + potential per row
  { key: 'numbered', label: 'Numbered' },    // number ranks instead of icons
  { key: 'minimal', label: 'Minimal' },      // light, glow-free, outlined cards
  { key: 'night', label: 'Night' },          // dark theme
]

const CLASSIC_THEME = {
  background: 'glow',
  wordmark: BRAND.ink,
  title: BRAND.ink,
  subtitle: BRAND.gray,
  footer: BRAND.royalPurple,
  cardFill: BRAND.lavender,
  cardBorder: null,
  winnerFill: 'gradient',
  rankStyle: 'icon',
  rankColor: BRAND.royalPurple,
  rankColorWinner: BRAND.royalPurple,
  name: BRAND.ink,
  nameWinner: BRAND.ink,
  points: BRAND.ink,
  pointsWinner: BRAND.ink,
  stats: BRAND.gray,
  statsWinner: BRAND.night,
  youRing: BRAND.royalPurple,
  showStats: false,
}

function resolveTheme(variant) {
  switch (variant) {
    case 'detailed':
      return { ...CLASSIC_THEME, showStats: true }
    case 'numbered':
      return { ...CLASSIC_THEME, rankStyle: 'number' }
    case 'minimal':
      return {
        ...CLASSIC_THEME,
        background: 'plain',
        cardFill: BRAND.white,
        cardBorder: BRAND.lavender,
        winnerFill: BRAND.lemon,
        rankStyle: 'number',
        rankColor: BRAND.gray,
        rankColorWinner: BRAND.royalPurple,
      }
    case 'night':
      return {
        ...CLASSIC_THEME,
        background: 'dark',
        wordmark: BRAND.white,
        title: BRAND.white,
        subtitle: BRAND.lavender,
        footer: BRAND.lemon,
        cardFill: BRAND.night,
        winnerFill: 'gradient',
        rankColor: BRAND.lemon,
        rankColorWinner: BRAND.royalPurple,
        name: BRAND.lavender,
        nameWinner: BRAND.ink,
        points: BRAND.lemon,
        pointsWinner: BRAND.ink,
        stats: 'rgba(229, 213, 255, 0.72)',
        statsWinner: BRAND.night,
        youRing: BRAND.lemon,
      }
    case 'classic':
    default:
      return CLASSIC_THEME
  }
}

/**
 * Render a leaderboard share card. `rows` is an array of
 * { rank, full_name, total_points, is_current_user, accuracy?, potential_points? }.
 * `variant` selects a design from SHARE_CARD_VARIANTS. Returns a PNG Blob or null.
 */
export async function renderLeaderboardShareCard({ title = 'Who got the dub?', subtitle = '', rows = [], variant = 'classic' }) {
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = SIZE
  canvas.height = SIZE
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const theme = resolveTheme(variant)
  ctx.textBaseline = 'top'

  // ── Background ─────────────────────────────────────────────────────
  if (theme.background === 'dark') {
    ctx.fillStyle = BRAND.ink
    ctx.fillRect(0, 0, SIZE, SIZE)
    drawGlow(ctx, SIZE * 0.9, SIZE * 0.1, SIZE * 0.7, [
      [0, 'rgba(235, 255, 89, 0.18)'],
      [0.5, 'rgba(198, 255, 156, 0.08)'],
      [1, 'rgba(25, 21, 34, 0)'],
    ])
    drawGlow(ctx, SIZE * 0.08, SIZE * 0.98, SIZE * 0.7, [
      [0, 'rgba(108, 80, 155, 0.45)'],
      [1, 'rgba(25, 21, 34, 0)'],
    ])
  } else {
    ctx.fillStyle = BRAND.cream
    ctx.fillRect(0, 0, SIZE, SIZE)
    if (theme.background === 'glow') {
      drawGlow(ctx, SIZE * 0.88, SIZE * 0.12, SIZE * 0.75, [
        [0, 'rgba(235, 255, 89, 0.65)'],
        [0.35, 'rgba(198, 255, 156, 0.35)'],
        [0.75, 'rgba(246, 241, 241, 0.05)'],
        [1, 'rgba(246, 241, 241, 0)'],
      ])
      drawGlow(ctx, SIZE * 0.1, SIZE * 0.98, SIZE * 0.7, [
        [0, 'rgba(229, 213, 255, 0.85)'],
        [0.45, 'rgba(108, 80, 155, 0.28)'],
        [1, 'rgba(246, 241, 241, 0)'],
      ])
    }
  }

  // ── Wordmark + LEADERBOARD pill ────────────────────────────────────
  ctx.fillStyle = theme.wordmark
  ctx.font = `800 112px ${HEADING_FONT}`
  ctx.fillText('dub', PADDING, PADDING - 12)
  drawPillLabel(ctx, 'LEADERBOARD', PADDING, PADDING + 118)

  // ── Title ──────────────────────────────────────────────────────────
  ctx.fillStyle = theme.title
  ctx.font = `800 72px ${HEADING_FONT}`
  ctx.fillText(truncate(ctx, title, SIZE - PADDING * 2), PADDING, PADDING + 186)

  // Subtitle — wraps to a second line for long tournament/division strings.
  const SUBTITLE_LINE_HEIGHT = 42
  let subtitleLineCount = 0
  if (subtitle) {
    ctx.font = `500 34px ${BODY_FONT}`
    ctx.fillStyle = theme.subtitle
    const lines = wrapLines(ctx, subtitle, SIZE - PADDING * 2, 2)
    lines.forEach((line, idx) => {
      ctx.fillText(line, PADDING, PADDING + 272 + idx * SUBTITLE_LINE_HEIGHT)
    })
    subtitleLineCount = lines.length
  }

  // ── Rows ───────────────────────────────────────────────────────────
  const cardTop = PADDING + 340 + Math.max(0, subtitleLineCount - 1) * SUBTITLE_LINE_HEIGHT
  const cardHeight = 108
  const gap = 16
  const maxRows = Math.min(rows.length, 5)
  const cardX = PADDING
  const cardWidth = SIZE - PADDING * 2
  const radius = cardHeight / 2 // full pill

  for (let i = 0; i < maxRows; i++) {
    const r = rows[i]
    const y = cardTop + i * (cardHeight + gap)
    const isYou = !!r.is_current_user
    // Winner treatment follows actual rank, so TIED co-leaders both get the
    // celebratory treatment (matches the leaderboard sharing rank 1).
    const isWinner = Number(r.rank) === 1
    const midY = y + cardHeight / 2

    // User ring when the user isn't a winner.
    if (isYou && !isWinner) {
      const strokeW = 5
      ctx.fillStyle = theme.youRing
      roundedRect(ctx, cardX - strokeW, y - strokeW, cardWidth + strokeW * 2, cardHeight + strokeW * 2, radius + strokeW)
      ctx.fill()
    }

    // Card fill.
    if (isWinner && theme.winnerFill === 'gradient') {
      const grad = ctx.createLinearGradient(cardX, y, cardX + cardWidth, y + cardHeight)
      grad.addColorStop(0, BRAND.mint)
      grad.addColorStop(1, BRAND.lemon)
      ctx.fillStyle = grad
    } else if (isWinner) {
      ctx.fillStyle = theme.winnerFill
    } else {
      ctx.fillStyle = theme.cardFill
    }
    roundedRect(ctx, cardX, y, cardWidth, cardHeight, radius)
    ctx.fill()
    if (theme.cardBorder) {
      ctx.strokeStyle = theme.cardBorder
      ctx.lineWidth = 2
      roundedRect(ctx, cardX, y, cardWidth, cardHeight, radius)
      ctx.stroke()
    }

    ctx.textBaseline = 'middle'

    // Rank indicator: glyph (Crown/Medal/Award) for the top three, else the
    // number. 'numbered'/'minimal' themes always use the number. Keyed off the
    // actual rank so ties share an indicator.
    const rankNum = Number(r.rank)
    const rankColor = isWinner ? theme.rankColorWinner : theme.rankColor
    const glyphSize = 46
    const glyphX = cardX + 34
    if (theme.rankStyle === 'icon' && rankNum === 1) {
      drawCrown(ctx, glyphX, midY - glyphSize * 0.5, glyphSize, rankColor)
    } else if (theme.rankStyle === 'icon' && rankNum === 2) {
      drawMedal(ctx, glyphX, midY - glyphSize * 0.5, glyphSize, rankColor)
    } else if (theme.rankStyle === 'icon' && rankNum === 3) {
      drawAward(ctx, glyphX, midY - glyphSize * 0.5, glyphSize, rankColor)
    } else {
      ctx.font = `800 56px ${HEADING_FONT}`
      ctx.fillStyle = rankColor
      ctx.fillText(`${rankNum}`, cardX + 44, midY)
    }

    // Name (+ optional stats line beneath it for the 'detailed' theme).
    const nameX = cardX + 150
    const ptsReserve = 260
    const youBadgeReserve = isYou ? 96 : 0
    const nameMaxWidth = cardWidth - (nameX - cardX) - ptsReserve - youBadgeReserve
    const nameY = theme.showStats ? midY - 16 : midY

    ctx.font = `700 42px ${BODY_FONT}`
    ctx.fillStyle = isWinner ? theme.nameWinner : theme.name
    const displayName = truncate(ctx, r.full_name || 'Unknown', nameMaxWidth)
    ctx.fillText(displayName, nameX, nameY)

    if (theme.showStats) {
      const bits = []
      if (r.accuracy != null) bits.push(`${Number(r.accuracy).toFixed(1)}% accuracy`)
      if (r.potential_points != null) bits.push(`+${Number(r.potential_points).toLocaleString()} potential`)
      if (bits.length) {
        ctx.font = `500 26px ${BODY_FONT}`
        ctx.fillStyle = isWinner ? theme.statsWinner : theme.stats
        ctx.fillText(bits.join('   ·   '), nameX, midY + 22)
      }
    }

    // Inline "YOU" badge after the name.
    if (isYou) {
      ctx.font = `700 42px ${BODY_FONT}`
      const nameW = ctx.measureText(displayName).width
      const badgePadX = 12
      ctx.font = `800 18px ${BODY_FONT}`
      const badgeText = 'YOU'
      const badgeW = ctx.measureText(badgeText).width + badgePadX * 2
      const badgeH = 34
      const badgeX = nameX + nameW + 14
      ctx.fillStyle = theme.youRing
      roundedRect(ctx, badgeX, nameY - badgeH / 2, badgeW, badgeH, badgeH / 2)
      ctx.fill()
      ctx.fillStyle = theme.background === 'dark' ? BRAND.ink : BRAND.white
      ctx.fillText(badgeText, badgeX + badgePadX, nameY)
    }

    // Points — right-aligned.
    const rightEdge = cardX + cardWidth - 36
    ctx.font = `800 56px ${HEADING_FONT}`
    ctx.fillStyle = isWinner ? theme.pointsWinner : theme.points
    const ptsText = `${Number(r.total_points || 0).toLocaleString()}`
    const ptsWidth = ctx.measureText(ptsText).width
    ctx.fillText(ptsText, rightEdge - ptsWidth, midY)

    ctx.textBaseline = 'top'
  }

  // ── Footer ─────────────────────────────────────────────────────────
  ctx.font = `700 28px ${BODY_FONT}`
  ctx.fillStyle = theme.footer
  const footerText = 'getdub.com'
  const footerWidth = ctx.measureText(footerText).width
  const lastRowBottom = cardTop + (maxRows - 1) * (cardHeight + gap) + cardHeight
  const footerY = lastRowBottom + 36
  ctx.fillText(footerText, (SIZE - footerWidth) / 2, footerY)

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png', 0.95)
  })
}
